import {Dispatch} from 'redux';

import {SalesManager, SalesRepresentative} from 'Component/Chat/Chat.type';
import CustomerDeliveryDateQuery from 'Query/CustomerDeliveryDate.query';
import {Customer} from 'Query/MyAccount.type';
import MyAccountCompanyQuery from 'Query/MyAccountCompany.query';
import {awCaGetCompanyForCurrentCustomer, companyUsersOutput} from 'Query/MyAccountCompany.type';
import SalesManagerQuery from 'Query/SalesManager.query';
import SalesRepresentativeQuery from 'Query/SalesRepresentative.query';
import {CUSTOMER} from 'Route/MyAccount/MyAccount.config';
import {
    updateAccountCompanyInfo,
    updateCompanyUsersList,
    updateSalesManager,
    updateSalesRepresentative, updateSelectedDeliveryDay,
} from 'Store/MyAccountCompany/MyAccountCompany.action';
import {showNotification} from 'Store/Notification/Notification.action';
import {NotificationType} from 'Store/Notification/Notification.type';
import {getAuthorizationToken} from 'Util/Auth/Token';
import BrowserDatabase from 'Util/BrowserDatabase';
import {formattedDayEurope} from 'Util/DateFormat';
import {prepareQuery} from 'Util/Query';
import {ONE_MONTH_IN_SECONDS} from 'Util/Request/Config';
import {getErrorMessage} from 'Util/Request/Error';
import {fetchMutation} from 'Util/Request/Mutation';
import {executePost} from 'Util/Request/Request';

/** @namespace Steinkrueger/Store/MyAccountCompany/Dispatcher */
export class MyAccountCompanyDispatcher {
    getCompanyInfo(dispatch: Dispatch): Promise<void> {
        const query = [MyAccountCompanyQuery.getQuery()];

        return executePost<{
            awCaGetCompanyForCurrentCustomer: awCaGetCompanyForCurrentCustomer;
        }>(prepareQuery(query)).then(
            /** @namespace Steinkrueger/Store/MyAccountCompany/Dispatcher/MyAccountCompanyDispatcher/getCompanyInfo/executePost/then */
            (data) => {
                const {awCaGetCompanyForCurrentCustomer} = data;

                if (awCaGetCompanyForCurrentCustomer) {
                    dispatch(updateAccountCompanyInfo(awCaGetCompanyForCurrentCustomer));
                } else {
                    this.clearData(dispatch);
                }
            },
            /** @namespace Steinkrueger/Store/MyAccountCompany/Dispatcher/MyAccountCompanyDispatcher/getCompanyInfo/executePost/then/catch */
            (error) => {
                this.clearData(dispatch);
                showNotification(NotificationType.ERROR, __('Error fetching Company Information'), error);
            },
        );
    }

    getSalesManager(dispatch: Dispatch): Promise<void> {
        const query = [SalesManagerQuery.getQuery()];

        return executePost<{
            getSalesManager: SalesManager;
        }>(prepareQuery(query)).then(
            /** @namespace Steinkrueger/Store/MyAccountCompany/Dispatcher/MyAccountCompanyDispatcher/getSalesManager/executePost/then */
            (data) => {
                const { getSalesManager } = data;

                if (getSalesManager) {
                    dispatch(updateSalesManager(getSalesManager));
                } else {
                    this.clearData(dispatch);
                }
            },
            /** @namespace Steinkrueger/Store/MyAccountCompany/Dispatcher/MyAccountCompanyDispatcher/getSalesManager/executePost/then/catch */
            (error) => {
                this.clearData(dispatch);
                showNotification(NotificationType.ERROR, __('Error fetching manager Information'), error);
            },
        );
    }

    getSalesRepresentative(dispatch: Dispatch): Promise<void> {
        const query = [SalesRepresentativeQuery.getQuery()];

        return executePost<{
            getSalesRepresentative: SalesRepresentative;
        }>(prepareQuery(query)).then(
            /** @namespace Steinkrueger/Store/MyAccountCompany/Dispatcher/MyAccountCompanyDispatcher/getSalesRepresentative/executePost/then */
            (data) => {
                const { getSalesRepresentative } = data;

                if (getSalesRepresentative) {
                    dispatch(updateSalesRepresentative(getSalesRepresentative));
                } else {
                    this.clearData(dispatch);
                }
            },
            /** @namespace Steinkrueger/Store/MyAccountCompany/Dispatcher/MyAccountCompanyDispatcher/getSalesRepresentative/executePost/then/catch */
            (error) => {
                this.clearData(dispatch);
                showNotification(NotificationType.ERROR, __('Error fetching Company Information'), error);
            },
        );
    }

    getCompanyUsersList(dispatch: Dispatch): Promise<void> {
        const options = {
            currentPage: 1,
            pageSize: 20,
        };

        const query = [MyAccountCompanyQuery.getUsers(options)];

        return executePost<{awCaCompanyUserList: companyUsersOutput}>((prepareQuery(query))).then(
            /** @namespace Steinkrueger/Store/MyAccountCompany/Dispatcher/MyAccountCompanyDispatcher/getCompanyUsersList/executePost/then */
            (data) => {
                const { awCaCompanyUserList } = data;

                if (awCaCompanyUserList) {
                    dispatch(updateCompanyUsersList(awCaCompanyUserList));
                } else {
                    this.clearData(dispatch);
                }
            },
            /** @namespace Steinkrueger/Store/MyAccountCompany/Dispatcher/MyAccountCompanyDispatcher/getCompanyUsersList/executePost/then/catch */
            (error) => {
                this.clearData(dispatch);
                showNotification(NotificationType.ERROR, __('Error fetching company users information'), error);
            },
        );
    }

    getSelectedDeliveryDay(dispatch: Dispatch): Promise<void> {
        const query = [CustomerDeliveryDateQuery.getCustomerDeliveryDate()];

        return executePost<{ customer: Customer }>(prepareQuery(query)).then(
            /** @namespace Steinkrueger/Store/MyAccountCompany/Dispatcher/MyAccountCompanyDispatcher/getSelectedDeliveryDay/executePost/then */
            (data) => {
                const { customer } = data;

                if (!getAuthorizationToken()) {
                    return;
                }

                if (customer.delivery_date) {
                    const customerDeliveryDate = new Date(customer.delivery_date);
                    const today = new Date();

                    if (today < customerDeliveryDate) {
                        const formattedDate = formattedDayEurope(customer.delivery_date);
                        dispatch(updateSelectedDeliveryDay(formattedDate));
                    } else {
                        const nextTomorrowDeliveryDay = new Date(today);
                        nextTomorrowDeliveryDay.setDate(today.getDate() + 1);
                        dispatch(updateSelectedDeliveryDay(formattedDayEurope(nextTomorrowDeliveryDay.toString())));
                    }
                }
            },
            /** @namespace Steinkrueger/Store/MyAccountCompany/Dispatcher/MyAccountCompanyDispatcher/getSelectedDeliveryDay/executePost/then/catch */
            (error) => {
                dispatch(showNotification(NotificationType.ERROR, getErrorMessage(error)));
            },
        );
    }

    async setSelectedDeliveryDay(dispatch: Dispatch, date: string): Promise<void> {
        const formattedDate = formattedDayEurope(date);
        // eslint-disable-next-line max-len
        const mutation = [CustomerDeliveryDateQuery.setCustomerDeliveryDateMutation({deliveryDate: formattedDate})];
        dispatch(updateSelectedDeliveryDay(date));

        try {
            await fetchMutation(mutation);
            const customerData = BrowserDatabase.getItem(CUSTOMER) || {
                delivery_date: '',
            };
            // @ts-ignore
            customerData.delivery_date = formattedDate;
            BrowserDatabase.setItem(customerData, CUSTOMER, ONE_MONTH_IN_SECONDS);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    }

    setPreSelectedDeliveryDay(dispatch: Dispatch, date: string): void {
        dispatch(updateSelectedDeliveryDay(date));
    }

    clearData(dispatch: Dispatch): void {
        const awCaGetCompanyForCurrentCustomer = {
            name: '',
            email: '',
            street: '',
            city: '',
            region: '',
            postcode: '',
            country_id: '',
            telephone: '',
            sales_representative_id: '',
            admin_info: {
                firstname: '',
                lastname: '',
                email: '',
                job_title: '',
                telephone: '',
            },
        };

        const companyUsersList = {
            items: [],
            page_info: {
                current_page: 0,
                page_size: 0,
            },
            total_count: 0,
        };

        dispatch(updateAccountCompanyInfo(awCaGetCompanyForCurrentCustomer));
        dispatch(updateCompanyUsersList(companyUsersList));
        dispatch(updateSelectedDeliveryDay(''));
    }
}
export default new MyAccountCompanyDispatcher();
